import React, {useState, useRef, useEffect} from 'react';
import {
  Editor,
  Transforms,
  Path,
  Node as SlateNode,
  Text,
  Element as SlateElement
} from 'slate';
import {useSlateStatic, ReactEditor} from 'slate-react';

import {AnchorOptionsMenuProps} from './constants/ReportBuilderInterfce';

const AnchorOptionsMenu: React.FC<AnchorOptionsMenuProps> = ({element}) => {
  const editor = useSlateStatic();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isParagraph = (node: any) =>
    !Editor.isEditor(node) &&
    SlateElement.isElement(node) &&
    node.type === 'paragraph';
  const cloneDeep = (value: any) => JSON.parse(JSON.stringify(value));
  const hasMeaningfulParagraphContent = (children: any[] = []) =>
    children.some(
      (child: any) =>
        !Text.isText(child) || String(child.text || '').trim().length > 0
    );

  const moveIntoInlineFlow = (path: number[]) => {
    Editor.withoutNormalizing(editor, () => {
      const parentPath = Path.parent(path);
      const index = path[path.length - 1];
      const currentNode = SlateNode.get(editor, path) as any;
      const inlineNode = {...cloneDeep(currentNode), float: 'inline'};

      const prevPath = index > 0 ? [...parentPath, index - 1] : null;
      const nextPath = [...parentPath, index + 1];
      const prevNode =
        prevPath && SlateNode.has(editor, prevPath)
          ? (SlateNode.get(editor, prevPath) as any)
          : null;
      const nextNode = SlateNode.has(editor, nextPath)
        ? (SlateNode.get(editor, nextPath) as any)
        : null;

      if (prevPath && isParagraph(prevNode)) {
        Transforms.removeNodes(editor, {at: path});

        const prevParagraph = SlateNode.get(editor, prevPath) as any;
        const insertAt = [...prevPath, prevParagraph.children.length];
        Transforms.insertNodes(editor, inlineNode, {at: insertAt});

        // If next sibling is a paragraph, merge it so text continues after the inline object.
        const shiftedNextPath = [...parentPath, index];
        if (SlateNode.has(editor, shiftedNextPath)) {
          const shiftedNextNode = SlateNode.get(editor, shiftedNextPath) as any;
          if (isParagraph(shiftedNextNode)) {
            const nextChildren = Array.isArray(shiftedNextNode.children)
              ? shiftedNextNode.children.map((child: any) => cloneDeep(child))
              : [];
            if (nextChildren.length > 0) {
              const updatedPrev = SlateNode.get(editor, prevPath) as any;
              const appendAt = [...prevPath, updatedPrev.children.length];
              Transforms.insertNodes(editor, nextChildren, {at: appendAt});
            }
            Transforms.removeNodes(editor, {at: shiftedNextPath});
          }
        }

        return;
      }

      if (isParagraph(nextNode)) {
        Transforms.removeNodes(editor, {at: path});
        const shiftedNextPath = [...parentPath, index];
        if (SlateNode.has(editor, shiftedNextPath)) {
          const targetParagraph = SlateNode.get(editor, shiftedNextPath) as any;
          if (isParagraph(targetParagraph)) {
            Transforms.insertNodes(editor, inlineNode, {
              at: [...shiftedNextPath, 0]
            });
          }
        }

        return;
      }

      // No adjacent text blocks: create an inline-capable paragraph around the node.
      Transforms.removeNodes(editor, {at: path});
      Transforms.insertNodes(
        editor,
        {
          type: 'paragraph',
          children: [{text: ''}, inlineNode, {text: ''}]
        },
        {at: path}
      );
    });
  };

  const moveOutOfInlineFlow = (
    path: number[],
    floatValue: 'left' | 'right' | 'break'
  ) => {
    Editor.withoutNormalizing(editor, () => {
      const parentPath = Path.parent(path);
      if (!SlateNode.has(editor, parentPath)) {
        if (SlateNode.has(editor, path)) {
          Transforms.setNodes(editor, {float: floatValue}, {at: path});
        }

        return;
      }

      const parentNode = SlateNode.get(editor, parentPath) as any;
      if (!isParagraph(parentNode)) {
        Transforms.setNodes(editor, {float: floatValue}, {at: path});

        return;
      }

      const inlineIndex = path[path.length - 1];
      const paragraphChildren = Array.isArray(parentNode.children)
        ? parentNode.children
        : [];
      const currentNode = SlateNode.get(editor, path) as any;
      const blockNode = {...cloneDeep(currentNode), float: floatValue};

      const beforeChildren = paragraphChildren
        .slice(0, inlineIndex)
        .map((child: any) => cloneDeep(child));
      const afterChildren = paragraphChildren
        .slice(inlineIndex + 1)
        .map((child: any) => cloneDeep(child));

      const replacementNodes: any[] = [];
      if (hasMeaningfulParagraphContent(beforeChildren)) {
        replacementNodes.push({type: 'paragraph', children: beforeChildren});
      }

      replacementNodes.push(blockNode);

      if (hasMeaningfulParagraphContent(afterChildren)) {
        replacementNodes.push({type: 'paragraph', children: afterChildren});
      }

      // Keep at least one editable paragraph near the block for caret continuity.
      if (replacementNodes.length === 1) {
        replacementNodes.push({type: 'paragraph', children: [{text: ''}]});
      }

      Transforms.removeNodes(editor, {at: parentPath});
      Transforms.insertNodes(editor, replacementNodes, {at: parentPath});
    });
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as any)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (
    floatValue: 'left' | 'right' | 'inline' | 'break'
  ) => {
    const path = ReactEditor.findPath(editor, element);

    if (floatValue === 'inline') {
      moveIntoInlineFlow(path as any);
      closeMenu();

      return;
    }

    moveOutOfInlineFlow(path as any, floatValue);
    closeMenu();
  };

  return (
    <div
      className="anchor-options-container"
      contentEditable={false}
      ref={menuRef}
    >
      <div
        className={`anchor-trigger ${isOpen ? 'active' : ''}`}
        onMouseDown={toggleMenu}
        title="Layout Options"
      >
        {/* 3 Vertical Dots Icon */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="1"></circle>
          <circle cx="12" cy="5" r="1"></circle>
          <circle cx="12" cy="19" r="1"></circle>
        </svg>
      </div>

      {isOpen && (
        <div className="anchor-menu">
          <div
            className="anchor-menu-item"
            onMouseDown={() => {
              handleOptionClick('inline');
            }}
          >
            Inline With Text
          </div>
          <div
            className="anchor-menu-item"
            onMouseDown={() => {
              handleOptionClick('left');
            }}
          >
            Wrap Left
          </div>
          <div
            className="anchor-menu-item"
            onMouseDown={() => {
              handleOptionClick('right');
            }}
          >
            Wrap Right
          </div>
          <div
            className="anchor-menu-item"
            onMouseDown={() => {
              handleOptionClick('break');
            }}
          >
            Break Text
          </div>
        </div>
      )}
    </div>
  );
};

export default AnchorOptionsMenu;
